import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success(res.data.message || "Email envoyé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur s'est produite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-8" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Retour à la connexion
        </Link>

        {!sent ? (
          <>
            <div className="mb-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-sky-100 flex items-center justify-center mx-auto mb-3">
                <Mail className="w-6 h-6 text-sky-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800">Mot de passe oublié ?</h1>
              <p className="text-sm text-slate-500 mt-2">
                Saisissez l'email associé à votre compte. Vous recevrez un lien sécurisé pour créer un nouveau mot de passe.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  required
                  autoFocus
                  data-testid="forgot-email-input"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-slate-800 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none transition"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full bg-sky-600 hover:bg-sky-500 text-white font-semibold py-2.5 gap-2"
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Envoyer le lien de réinitialisation
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Vérifiez votre boîte mail</h2>
            <p className="text-sm text-slate-500 mt-3">
              Si un compte existe pour <strong>{email}</strong>, un email avec les instructions vient d'être envoyé.
            </p>
            <p className="text-xs text-slate-400 mt-3">
              Le lien expire dans <strong>60 minutes</strong>. Pensez à vérifier vos spams si vous ne recevez rien.
            </p>
            <Link to="/login">
              <Button className="mt-6 bg-slate-800 hover:bg-slate-700 text-white" data-testid="back-to-login-after-sent">
                Retour à la connexion
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
